"use client"

import { useEffect, useMemo, useState } from "react"
import { useTranslations } from "next-intl"
import { Search } from "lucide-react"
import { cn } from "@/lib/utils"
import { filterChatEmojiCatalog } from "@/lib/chat/chat-emoji-catalog"
import { useMobile } from "@/hooks/use-mobile"
import { DockSheet } from "@/components/ui/dock-sheet"
import { DialogCard } from "@/components/ui/dialog-card"
import {
  FIELD_SURFACE_SEARCH_SHELL_CLASS,
  SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS,
} from "@/lib/dropdown-styles"
import { SHEET_Z_PICKER } from "@/lib/sheet-styles"

const EMOJI_COLUMNS = 7
const EMOJI_CELL_SIZE = 44
const PICKER_LIST_PADDING = "px-3"

export type ChatEmojiPickerSheetProps = {
  visible: boolean
  onClose: () => void
  onSelect: (emoji: string) => void
}

function ChatEmojiPickerSearch({
  query,
  onQueryChange,
}: {
  query: string
  onQueryChange: (value: string) => void
}) {
  const tChat = useTranslations("chat")

  return (
    <div className={cn(FIELD_SURFACE_SEARCH_SHELL_CLASS, "flex items-center gap-2 px-3")}>
      <Search className={cn("h-4 w-4 shrink-0", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)} />
      <input
        type="search"
        value={query}
        onChange={(e) => onQueryChange(e.target.value)}
        placeholder={tChat("emojiPickerSearch")}
        className="min-w-0 flex-1 bg-transparent text-base text-foreground outline-none placeholder:text-muted-foreground"
        aria-label={tChat("emojiPickerSearch")}
      />
    </div>
  )
}

function ChatEmojiPickerGrid({
  query,
  onSelect,
  className,
}: {
  query: string
  onSelect: (emoji: string) => void
  className?: string
}) {
  const tChat = useTranslations("chat")
  const data = useMemo(() => filterChatEmojiCatalog(query), [query])

  return (
    <div className={cn("min-h-0 flex-1 overflow-y-auto pb-3", className)}>
      {data.length === 0 ? (
        <p className={cn("py-8 text-center text-sm", SEARCHABLE_DROPDOWN_MUTED_TEXT_CLASS)}>
          {tChat("emojiPickerEmpty")}
        </p>
      ) : (
        <div
          className="grid gap-0"
          style={{ gridTemplateColumns: `repeat(${EMOJI_COLUMNS}, minmax(0, 1fr))` }}
        >
          {data.map((entry) => (
            <button
              key={entry.emoji}
              type="button"
              onClick={() => onSelect(entry.emoji)}
              className="flex items-center justify-center rounded-full text-2xl transition-colors hover:bg-sheet-handle active:scale-95 active:bg-foreground/20"
              style={{ height: EMOJI_CELL_SIZE }}
              aria-label={tChat("reactA11y", { emoji: entry.emoji })}
            >
              {entry.emoji}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

export function ChatEmojiPickerSheet({ visible, onClose, onSelect }: ChatEmojiPickerSheetProps) {
  const tChat = useTranslations("chat")
  const isMobile = useMobile()
  const [query, setQuery] = useState("")

  useEffect(() => {
    if (!visible) setQuery("")
  }, [visible])

  const handleSelect = (emoji: string) => {
    onSelect(emoji)
  }

  if (isMobile) {
    return (
      <DockSheet
        open={visible}
        onOpenChange={(open) => {
          if (!open) onClose()
        }}
        title={tChat("emojiPickerTitle")}
        belowTitle={
          <div className="-mx-1">
            <ChatEmojiPickerSearch query={query} onQueryChange={setQuery} />
          </div>
        }
        zIndex={SHEET_Z_PICKER}
        scrollManagedByChild
        contentClassName={cn("flex min-h-0 flex-col overflow-hidden pt-0", PICKER_LIST_PADDING)}
        height="72dvh"
      >
        <ChatEmojiPickerGrid query={query} onSelect={handleSelect} />
      </DockSheet>
    )
  }

  return (
    <DialogCard
      open={visible}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
      nested
      title={tChat("emojiPickerTitle")}
      /* Same slot as the sheet twin: the search field is chrome-adjacent
         context, not the first row of the scrolling grid. */
      belowTitle={<ChatEmojiPickerSearch query={query} onQueryChange={setQuery} />}
      panelClassName="max-w-md max-h-[min(72dvh,640px)]"
      contentClassName={cn("flex min-h-0 flex-col overflow-hidden pt-0", PICKER_LIST_PADDING)}
    >
      <ChatEmojiPickerGrid query={query} onSelect={handleSelect} />
    </DialogCard>
  )
}
